import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { theme } from '../../lib/theme'

// ── WebAuthn helpers ──────────────────────────────────────────
const bufferToBase64 = (buffer) =>
  btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')

const base64ToBuffer = (base64) => {
  const padded = base64.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(padded)
  return Uint8Array.from(binary, c => c.charCodeAt(0)).buffer
}

const isWebAuthnSupported = () =>
  window.PublicKeyCredential !== undefined &&
  typeof window.PublicKeyCredential === 'function'

export default function Login() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [hasBiometric, setHasBiometric] = useState(false)
  const [biometricLoading, setBiometricLoading] = useState(false)
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false)
  const [pendingUserId, setPendingUserId] = useState(null)

  useEffect(() => {
    // Check if this device has a stored credential
    const credId = localStorage.getItem('mv_cred_id')
    const savedEmail = localStorage.getItem('mv_cred_email')
    if (credId && savedEmail && isWebAuthnSupported()) {
      setHasBiometric(true)
      setEmail(savedEmail)
    }
  }, [])

  // ── BIOMETRIC LOGIN ──────────────────────────────────────────
  const handleBiometricLogin = async () => {
    setBiometricLoading(true)
    setError('')
    try {
      const credId = localStorage.getItem('mv_cred_id')
      if (!credId) { setError('No biometric found. Please sign in with password.'); setBiometricLoading(false); return }

      const challenge = crypto.getRandomValues(new Uint8Array(32))

      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge,
          allowCredentials: [{
            id: base64ToBuffer(credId),
            type: 'public-key',
          }],
          userVerification: 'required',
          timeout: 60000,
        }
      })

      if (!assertion) { setError('Biometric verification failed. Try password instead.'); setBiometricLoading(false); return }

      // Verify credential exists in DB and get user
      const credentialId = bufferToBase64(assertion.rawId)
      const { data: cred } = await supabase
        .from('webauthn_credentials')
        .select('user_id')
        .eq('credential_id', credentialId)
        .single()

      if (!cred) { setError('Biometric not recognized. Please sign in with password.'); setBiometricLoading(false); return }

      // Sign in using stored session magic — use custom token approach
      // Since WebAuthn doesn't directly give us a Supabase session,
      // we use the stored email + ask user to confirm with password if session expired
      const savedEmail = localStorage.getItem('mv_cred_email')
      const savedToken = localStorage.getItem('mv_refresh_token')

      if (savedToken) {
        const { data, error: refreshError } = await supabase.auth.refreshSession({ refresh_token: savedToken })
        if (!refreshError && data.session) {
          localStorage.setItem('mv_refresh_token', data.session.refresh_token)
          navigate('/')
          return
        }
      }

      // Refresh token expired — biometric verified but need new session
      setError('Session expired. Please sign in with your password once to re-enable Face ID.')
      setBiometricLoading(false)

    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Biometric cancelled. Enter your password below.')
      } else {
        setError('Biometric failed. Please sign in with password.')
      }
      setBiometricLoading(false)
    }
  }

  // ── PASSWORD LOGIN ───────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password })

    if (signInError) {
      setError(signInError.message)
      setLoading(false)
      return
    }

    // Store refresh token for biometric sessions
    if (data.session) {
      localStorage.setItem('mv_refresh_token', data.session.refresh_token)
    }

    // Check if biometric is supported and not already set up for this device
    if (isWebAuthnSupported() && data.user) {
      const credId = localStorage.getItem('mv_cred_id')
      if (!credId) {
        // Check if they have any credentials at all
        const { data: creds } = await supabase
          .from('webauthn_credentials')
          .select('id')
          .eq('user_id', data.user.id)
          .limit(1)

        if (!creds || creds.length === 0) {
          // New device — prompt to set up biometric
          setPendingUserId(data.user.id)
          setShowBiometricPrompt(true)
          setLoading(false)
          return
        }
      }
    }

    navigate('/')
  }

  // ── REGISTER BIOMETRIC ───────────────────────────────────────
  const handleEnableBiometric = async () => {
    try {
      setLoading(true)
      const { data: { user } } = await supabase.auth.getUser()
      const userId = pendingUserId || user?.id
      if (!userId) { navigate('/'); return }

      const challenge = crypto.getRandomValues(new Uint8Array(32))
      const userIdBytes = new TextEncoder().encode(userId)

      const credential = await navigator.credentials.create({
        publicKey: {
          challenge,
          rp: { name: 'Merq Vault', id: window.location.hostname },
          user: {
            id: userIdBytes,
            name: email,
            displayName: email,
          },
          pubKeyCredParams: [
            { alg: -7, type: 'public-key' },   // ES256
            { alg: -257, type: 'public-key' },  // RS256
          ],
          authenticatorSelection: {
            authenticatorAttachment: 'platform',
            userVerification: 'required',
            residentKey: 'preferred',
          },
          timeout: 60000,
        }
      })

      if (!credential) { navigate('/'); return }

      const credentialId = bufferToBase64(credential.rawId)
      const publicKey = bufferToBase64(credential.response.getPublicKey?.() || new ArrayBuffer(0))

      // Save to Supabase
      await supabase.from('webauthn_credentials').insert({
        user_id: userId,
        credential_id: credentialId,
        public_key: publicKey,
        counter: 0,
      })

      // Save to localStorage for this device
      localStorage.setItem('mv_cred_id', credentialId)
      localStorage.setItem('mv_cred_email', email)

      navigate('/')
    } catch (err) {
      // User cancelled or error — just proceed to app
      navigate('/')
    }
  }

  const handleSkipBiometric = () => {
    navigate('/')
  }

  // ── BIOMETRIC SETUP PROMPT ───────────────────────────────────
  if (showBiometricPrompt) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.biometricIcon}>
            {/iPhone|iPad|Mac/.test(navigator.userAgent) ? '👤' : '🔐'}
          </div>
          <div style={styles.biometricTitle}>
            Enable {/iPhone|iPad/.test(navigator.userAgent) ? 'Face ID' : /Mac/.test(navigator.userAgent) ? 'Touch ID' : 'Biometric'} Login?
          </div>
          <div style={styles.biometricDesc}>
            Skip the password next time. Use your {/iPhone|iPad/.test(navigator.userAgent) ? 'Face ID' : /Mac/.test(navigator.userAgent) ? 'fingerprint' : 'biometric'} to sign in instantly — faster, easier, and just as secure.
          </div>
          <button style={styles.biometricEnableBtn} onClick={handleEnableBiometric} disabled={loading}>
            {loading ? 'Setting up...' : `✓ Enable ${/iPhone|iPad/.test(navigator.userAgent) ? 'Face ID' : /Mac/.test(navigator.userAgent) ? 'Touch ID' : 'Biometric Login'}`}
          </button>
          <button style={styles.biometricSkipBtn} onClick={handleSkipBiometric}>
            Not now
          </button>
        </div>
      </div>
    )
  }

  // ── MAIN LOGIN FORM ──────────────────────────────────────────
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <span style={styles.logoMerq}>Merq</span>
          <span style={styles.logoVault}> Vault</span>
        </div>
        <p style={styles.tagline}>Your financial life, finally simple.</p>

        {/* BIOMETRIC BUTTON — shown if device has stored credential */}
        {hasBiometric && (
          <div style={styles.biometricSection}>
            <button
              style={styles.biometricBtn}
              onClick={handleBiometricLogin}
              disabled={biometricLoading}
            >
              <span style={styles.biometricBtnIcon}>
                {/iPhone|iPad/.test(navigator.userAgent) ? '👤' : /Mac/.test(navigator.userAgent) ? '✋' : '🔐'}
              </span>
              {biometricLoading ? 'Verifying...' : (
                /iPhone|iPad/.test(navigator.userAgent) ? 'Sign in with Face ID' :
                /Mac/.test(navigator.userAgent) ? 'Sign in with Touch ID' :
                'Sign in with Biometrics'
              )}
            </button>
            <div style={styles.orDivider}>
              <div style={styles.orLine} />
              <span style={styles.orText}>or use password</span>
              <div style={styles.orLine} />
            </div>
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="email">Email</label>
            <input
              id="email"
              style={styles.input}
              type="email"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              aria-label="Email address"
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="password">Password</label>
            <input
              id="password"
              style={styles.input}
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              aria-label="Password"
            />
          </div>
          {error && <div style={styles.error} role="alert">{error}</div>}
          <button style={styles.button} type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <div style={styles.links}>
          <Link to="/forgot-password" style={styles.link}>Forgot password?</Link>
        </div>
        <div style={styles.signup}>
          Don't have an account?{' '}
          <Link to="/signup" style={styles.link}>Start free — 45 days on us</Link>
        </div>
      </div>
    </div>
  )
}

const c = theme.colors
const f = theme.fonts

const styles = {
  container: {
    minHeight: '100vh',
    background: c.cream,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: f.body,
  },
  card: {
    background: c.white,
    borderRadius: '16px',
    padding: '40px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: theme.shadows.pop,
  },
  logo: { fontSize: '32px', fontWeight: '600', marginBottom: '8px', textAlign: 'center' },
  logoMerq: { color: c.plum, fontFamily: f.body, fontWeight: 700 },
  logoVault: { color: c.green, fontFamily: f.display, fontStyle: 'italic', fontWeight: 700 },
  tagline: { color: c.inkMuted, textAlign: 'center', marginBottom: '32px', fontSize: '14px' },
  biometricSection: { marginBottom: '20px' },
  biometricBtn: {
    width: '100%', padding: '14px', background: c.plum, color: c.cream,
    border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
    fontFamily: f.body,
  },
  biometricBtnIcon: { fontSize: '20px' },
  orDivider: { display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0 0' },
  orLine: { flex: 1, height: '1px', background: c.cream3 },
  orText: { fontSize: '12px', color: c.inkMuted, whiteSpace: 'nowrap' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: c.plum, marginBottom: '6px' },
  input: {
    width: '100%', padding: '12px', borderRadius: '8px',
    border: `1.5px solid ${c.cream3}`, fontSize: '14px',
    outline: 'none', boxSizing: 'border-box', color: c.plum,
    fontFamily: f.body,
  },
  error: { background: c.roseSoft, color: c.rose, padding: '10px 12px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' },
  button: {
    width: '100%', padding: '13px', background: c.green, color: c.white,
    border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', marginTop: '8px', fontFamily: f.body,
  },
  links: { textAlign: 'center', marginTop: '16px' },
  link: { color: c.green, textDecoration: 'none', fontSize: '13px' },
  signup: { textAlign: 'center', marginTop: '12px', fontSize: '13px', color: c.inkMuted },
  biometricIcon: { fontSize: '56px', textAlign: 'center', marginBottom: '16px' },
  biometricTitle: { fontSize: '22px', fontWeight: '700', color: c.plum, textAlign: 'center', marginBottom: '12px', fontFamily: f.display },
  biometricDesc: { fontSize: '14px', color: c.inkMuted, textAlign: 'center', lineHeight: '1.7', marginBottom: '28px' },
  biometricEnableBtn: {
    width: '100%', padding: '14px', background: c.green, color: c.white,
    border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600',
    cursor: 'pointer', marginBottom: '12px', fontFamily: f.body,
  },
  biometricSkipBtn: {
    width: '100%', padding: '12px', background: 'transparent', color: c.inkMuted,
    border: `1px solid ${c.cream3}`, borderRadius: '10px', fontSize: '14px',
    cursor: 'pointer', fontFamily: f.body,
  },
}
